import React, { useEffect, useMemo, useState } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import { CENIK_MAP, getCenikItem } from "../../data/cenikUtils";
import { LEFT_COLUMN, RIGHT_COLUMN, SLUZBY_SECTIONS, KREMACE_BEZ_OBRADU_NAVAZUJICI_URNA_SECTIONS } from "../../data/sluzbyLayout";
import { getRulesForTypZakazky, isServiceAllowedForTyp } from "../../data/sluzbyRules";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function calcRow(cenaBezDph, dphPct, mnozstvi) {
  const bez = round2((Number(cenaBezDph) || 0) * (Number(mnozstvi) || 0));
  const dph = round2((bez * (Number(dphPct) || 0)) / 100);
  return { bez, dph, celkem: round2(bez + dph) };
}

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 20,
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
};

const titleStyle = {
  fontSize: 30,
  fontWeight: 900,
  color: "#111827",
  marginBottom: 8,
};

const subStyle = {
  color: "#6b7280",
  fontSize: 15,
  marginBottom: 20,
};

const sectionStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 16,
  marginBottom: 16,
  background: "#fafafa",
};

const sectionTitleStyle = {
  fontSize: 18,
  fontWeight: 800,
  color: "#111827",
  marginBottom: 12,
};

const inputStyle = {
  width: 74,
  border: "1px solid #d1d5db",
  borderRadius: 10,
  padding: "8px 10px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
};

const thStyle = {
  textAlign: "left",
  fontSize: 13,
  color: "#374151",
  background: "#f3f4f6",
  borderBottom: "1px solid #e5e7eb",
  padding: "10px 8px",
};

const tdStyle = {
  borderBottom: "1px solid #e5e7eb",
  padding: "10px 8px",
  verticalAlign: "middle",
  fontSize: 14,
  color: "#111827",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "8px 12px",
  fontWeight: 800,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #93c5fd",
};

function formatMoney(n) {
  return `${round2(n).toFixed(2).replace(".", ",")} Kč`;
}

function isInvalidServicePrice(value) {
  const n = Number(value);
  return !Number.isFinite(n) || n <= 0;
}

function hasManualPrice(value) {
  return value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value));
}

function isIncompleteRakevRow(row) {
  if (String(row?.id || "").trim() !== "rakev") return false;
  const hasRakevId = !!String(row?.rakevId || "").trim();
  const hasOverride = hasManualPrice(row?.cenaBezDphOverride);
  return !hasRakevId && !hasOverride;
}

function safeStr(v) {
  return String(v || "").trim();
}

function pickFirst(...values) {
  for (const v of values) {
    if (safeStr(v)) return safeStr(v);
  }
  return "";
}

function toDateTime(dateStr, timeStr) {
  const raw = safeStr(dateStr);
  if (!raw) return null;

  const t = safeStr(timeStr) || "00:00";
  let normalized = raw;

  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(raw.replace(/\s+/g, ""))) {
    const compact = raw.replace(/\s+/g, "");
    const [dd, mm, yyyy] = compact.split(".");
    normalized = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }

  const x = new Date(`${normalized}T${t}`);
  return Number.isNaN(x.getTime()) ? null : x;
}


function getCoolingScenario(order = {}) {
  return String(
    order?.prevoz?.coolingScenario ||
    order?.prevoz?.bezObraduScenar ||
    ""
  ).trim();
}

function calcCoolingDays(order) {
  const vstupni = order?.vstupniInformace || {};
  const zesnula = order?.zesnulaOsoba || {};
  const prevoz = order?.prevoz || {};
  const obrad = order?.obrad || {};

  const umrtiDate = pickFirst(
    vstupni?.datumUmrti,
    zesnula?.datumUmrti
  );
  const umrtiTime = pickFirst(
    vstupni?.casUmrti,
    zesnula?.casUmrti
  );

  const startDate = pickFirst(
    prevoz?.datumDoChlazeni,
    prevoz?.datumUlozeni,
    prevoz?.datumPrevozu,
    vstupni?.datumDoChlazeni,
    vstupni?.datumUlozeni
  );
  const startTime = pickFirst(
    prevoz?.casDoChlazeni,
    prevoz?.casUlozeni,
    prevoz?.casPrevozu,
    vstupni?.casDoChlazeni,
    vstupni?.casUlozeni
  );

  const endDate = pickFirst(
    obrad?.datum,
    prevoz?.datumKonceChlazeni
  );
  const endTime = pickFirst(
    obrad?.cas,
    prevoz?.casKonceChlazeni
  );

  const scenario = getCoolingScenario(order);

  const umrti = toDateTime(umrtiDate, umrtiTime);
  const start = toDateTime(startDate, startTime);
  const end = toDateTime(endDate, endTime);

  if (!umrti || !start || !end) {
    return { dnyChlad: 0, dnyMraz: 0, ok: false, scenario };
  }

  if (end.getTime() <= start.getTime()) {
    return { dnyChlad: 0, dnyMraz: 0, ok: false, scenario };
  }

  // výchozí logika:
  // do 7. dne od úmrtí = chlazení
  // po 7. dni = mražení
  let cut = new Date(umrti.getTime() + 7 * 24 * 60 * 60 * 1000);

  // připravená větev pro speciální scénáře
  if (scenario === "wagnerka") {
    // zatím stejná hranice jako default
    // až doplníme přesné pravidlo ze staré Pohřebky, změníme to jen tady
    cut = new Date(umrti.getTime() + 7 * 24 * 60 * 60 * 1000);
  }

  const minEndCut = new Date(Math.min(end.getTime(), cut.getTime()));
  const maxStartCut = new Date(Math.max(start.getTime(), cut.getTime()));

  const chladMs = Math.max(0, minEndCut.getTime() - start.getTime());
  const mrazMs = Math.max(0, end.getTime() - maxStartCut.getTime());

  const dnyChlad =
    chladMs > 0 ? Math.max(0, Math.ceil(chladMs / (24 * 60 * 60 * 1000))) : 0;
  const dnyMraz =
    mrazMs > 0 ? Math.max(0, Math.ceil(mrazMs / (24 * 60 * 60 * 1000))) : 0;

  return { dnyChlad, dnyMraz, ok: true, scenario };
}

function getRakveList() {
  return Object.values(CENIK_MAP || {})
    .filter((item) => item && item.kategorie === "rakve")
    .sort((a, b) => String(a.nazev || "").localeCompare(String(b.nazev || ""), "cs"));
}


function getSectionSubgroups(sectionKey) {
  const map = {
    media: [
      {
        key: "foto_video",
        title: "Foto / video / záznam",
        ids: ["fotografie", "video", "zaznam_z_obradu"],
      },
      {
        key: "projekce",
        title: "Projekce a přenos",
        ids: ["promitani_fotografii", "priprava_prezentace_do30", "priprava_prezentace_nad30", "online_prenos"],
      },
    ],
    kvetiny: [
      {
        key: "zakladni_kvetiny",
        title: "Květiny a vazba",
        ids: ["kvetiny_vazba", "kvetinova_vyzdoba", "kytice", "vence", "stuhy"],
      },
      {
        key: "vystaveni_kvetin",
        title: "Vystavení a aranžmá",
        ids: ["vystaveni_fotografie", "vystaveni_kytice_ks", "vystaveni_vence_ks", "vystaveni_kvetinove_vyzdoby_1ks"],
      },
    ],
    urna: [
      {
        key: "urna_typ",
        title: "Typ urny",
        ids: ["urne_standard", "urne_lux", "uredni_urna"],
      },
      {
        key: "urna_ukony",
        title: "Úkony s urnou",
        ids: ["manipulace_urnou", "otevreni_a_zalepeni_urny", "odsyp_popela", "privezeni_urny_cb"],
      },
    ],
    obrad: [
      {
        key: "hudba_recnik",
        title: "Hudba a řečník",
        ids: [
          "reprodukovana_hudba",
          "reprodukovana_hudba_mimo_sin",
          "hra_na_varhany",
          "hra_varhany_s_dechovou",
          "zpev",
          "civilni_recnik_v_sini",
          "civilni_recnik_mimo_ck",
          "obradnik_v_sini_pouze_pri_hudbe",
          "obradnik_mimo_ck"
        ],
      },
      {
        key: "misto_a_vybava",
        title: "Místo a vybavení",
        ids: [
          "pronajem_obradni_sin",
          "pronajem_kaple",
          "katafalk_obradni_sin",
          "pojizdny_katafalk",
          "alternativni_misto_organizace",
          "asistence_alternativni_rozlouceni_hod",
          "obrad_s_urnou_zapujceni_rakve",
          "obrad_s_urnou_zapujceni_urny"
        ],
      },
    ],
    prevoz: [
      {
        key: "doprava",
        title: "Doprava",
        ids: ["doprava_1km", "prevoz_pausal_ck", "prevoz_krematorium_cb"],
      },
      {
        key: "chlazeni_mrazeni",
        title: "Chlazení a mražení",
        ids: ["chlazeni_24h", "mrazeni_24h"],
      },
      {
        key: "manipulace",
        title: "Manipulace",
        ids: ["manipulace_rakvi_nositky", "manipulace_rakvi_pat", "manipulace_telo_abn_rakev", "ulozeni"],
      },
    ],
    rakev: [
      {
        key: "rakev_priprava",
        title: "Rakev a příprava",
        ids: ["rakev", "vlozka_zinek", "souprava", "poduska", "krajka", "krizky"],
      },
      {
        key: "uprava_zesnuleho",
        title: "Úprava zesnulého",
        ids: ["rubas", "obleceni", "obleceni_rubase", "ustrojeni", "umyvani", "umyvani_obleceni_ulozeni_ustrojeni", "holeni", "liceni_malovani_nehtu"],
      },
    ],
    urna_navazujici: [
      {
        key: "urna_typ",
        title: "Typ urny",
        ids: ["urne_standard", "urne_lux", "uredni_urna"],
      },
      {
        key: "urna_ukony",
        title: "Úkony s urnou",
        ids: ["manipulace_urnou", "otevreni_a_zalepeni_urny", "odsyp_popela", "privezeni_urny_cb", "rizeni_pohrebu"],
      },
    ],
  };
  return map[sectionKey] || null;
}

function getDefaultServiceIdsForTyp(typZakazky) {
  const t = String(typZakazky || "").trim();

  if (t === "kremace") {
    return [
      "sjednani_obradu",
      "administrativa",
      "rakev",
      "manipulace_rakvi_nositky",
      "rizeni_pohrebu",
    ];
  }

  if (t === "kremace_bez_obradu") {
    return [
      "sjednani_zpopelneni_bez_obradu",
      "administrativa",
      "rakev",
      "manipulace_rakvi_nositky",
    ];
  }

  if (t === "pohreb") {
    return [
      "sjednani_obradu",
      "administrativa",
      "rakev",
      "souprava",
      "manipulace_rakvi_nositky",
      "rizeni_pohrebu",
    ];
  }

  if (t === "alternativa") {
    return [
      "sjednani_obradu",
      "administrativa",
      "rakev",
      "manipulace_rakvi_nositky",
      "rizeni_pohrebu",
      "alternativni_misto_organizace",
    ];
  }

  if (t === "obrad_s_urnou") {
    return [
      "sjednani_obradu",
      "administrativa",
      "manipulace_urnou",
      "rizeni_pohrebu",
    ];
  }

  if (t === "prevoz") {
    return [
      "administrativa",
      "manipulace_rakvi_nositky",
    ];
  }

  return [];
}

function getContextServiceIds(order = {}) {
  const zaklad = order?.zaklad || {};
  const obrad = order?.obrad || {};
  const typZakazky = String(zaklad?.typZakazky || "").trim();

  const mistoId = safeStr(obrad?.mistoId).toLowerCase();
  const mistoSkupina = safeStr(obrad?.mistoSkupina).toLowerCase();
  const ceremonyVariant = safeStr(obrad?.ceremonyVariant).toLowerCase();
  const churchServiceType = safeStr(obrad?.churchServiceType).toLowerCase();
  const fararJmeno = safeStr(obrad?.fararJmeno);
  const recnik = safeStr(obrad?.recnik);
  const hudba = safeStr(obrad?.hudba);
  const misto = safeStr(obrad?.misto).toLowerCase();

  const result = [];

  const isAlternativePlace =
    typZakazky === "alternativa" ||
    ceremonyVariant.includes("alternativ") ||
    mistoSkupina.includes("alternativ");

  const isChurch =
    !!fararJmeno ||
    mistoSkupina === "kostel" ||
    mistoId.includes("kostel") ||
    misto.includes("kostel") ||
    churchServiceType.includes("mse") ||
    churchServiceType.includes("bez_mse");

  const isCeremonyHall =
    mistoSkupina === "sin" ||
    mistoId.startsWith("sin_") ||
    misto.includes("obřadní síň") ||
    misto.includes("obradni sin");

  const hasSpeaker =
    !!recnik ||
    ceremonyVariant.includes("civil") ||
    ceremonyVariant.includes("recnik");

  const hasMusic = !!hudba;

  if (isAlternativePlace) {
    result.push("alternativni_misto_organizace");
  }

  if (hasSpeaker) {
    if (isCeremonyHall) {
      result.push("civilni_recnik_v_sini");
    } else {
      result.push("civilni_recnik_mimo_ck");
    }
  }

  if (hasMusic && !isChurch) {
    if (isCeremonyHall) {
      result.push("reprodukovana_hudba");
    } else {
      result.push("reprodukovana_hudba_mimo_sin");
    }
  }

  return Array.from(new Set(result));
}


function getDefaultOpenSubgroupKeys(typZakazky) {
  const t = String(typZakazky || "").trim();

  if (t === "kremace") {
    return [
      "prevoz::doprava",
      "prevoz::manipulace",
      "rakev::rakev_priprava",
      "obrad::misto_a_vybava",
    ];
  }

  if (t === "kremace_bez_obradu") {
    return [
      "prevoz::doprava",
      "prevoz::chlazeni_mrazeni",
      "prevoz::manipulace",
      "urna_navazujici::urna_typ",
      "urna_navazujici::urna_ukony",
    ];
  }

  if (t === "pohreb") {
    return [
      "prevoz::doprava",
      "prevoz::manipulace",
      "obrad::misto_a_vybava",
      "kvetiny::zakladni_kvetiny",
    ];
  }

  if (t === "alternativa") {
    return [
      "prevoz::doprava",
      "obrad::misto_a_vybava",
      "media::foto_video",
    ];
  }

  if (t === "obrad_s_urnou") {
    return [
      "urna::urna_typ",
      "urna::urna_ukony",
      "obrad::misto_a_vybava",
    ];
  }

  if (t === "prevoz") {
    return [
      "prevoz::doprava",
      "prevoz::chlazeni_mrazeni",
      "prevoz::manipulace",
    ];
  }

  return [];
}

function getDefaultOpenSectionKeys(typZakazky) {
  const t = String(typZakazky || "").trim();

  if (t === "kremace") return ["sjednani", "prevoz", "rakev"];
  if (t === "kremace_bez_obradu") return ["sjednani", "prevoz"];
  if (t === "pohreb") return ["sjednani", "prevoz", "obrad"];
  if (t === "alternativa") return ["sjednani", "prevoz", "obrad"];
  if (t === "obrad_s_urnou") return ["sjednani", "urna", "obrad"];
  if (t === "prevoz") return ["prevoz"];
  return [];
}

function getDopravaModeLabel(mode) {
  const m = String(mode || "").trim();
  if (m === "km") return "Skutečné km";
  if (m === "pausal_ck") return "Paušál po ČK";
  if (m === "pausal_cb") return "Paušál krematorium ČB";
  if (m === "combo_cb") return "Etapa 1 v km + paušál ČB";
  return m || "-";
}

function getDefaultDopravaMode(typZakazky, order = {}) {
  const t = String(typZakazky || "").trim();
  const scenar = String(order?.prevoz?.bezObraduScenar || "").trim();

  if (t === "kremace_bez_obradu") {
    if (scenar === "ck_nemocnice") return "pausal_cb";
    if (scenar === "doma_nebo_mimo_ck_nemocnici") return "combo_cb";
    return "pausal_cb";
  }

  if (t === "kremace") return "km";
  if (t === "pohreb") return "km";
  if (t === "alternativa") return "km";
  if (t === "obrad_s_urnou") return "km";
  if (t === "prevoz") return "km";
  return "km";
}

export default function Pohrebka2VyberSluzebForm() {
  const { order, setOrder, currentOrderId, allTemplates } = usePohrebka2();

  const resolveCenikItem = (id) => {
    const base = getCenikItem(id);
    const override = order?.cenikOverrides?.[id] || {};

    if (!base && (!override || typeof override !== "object" || override.__custom !== true)) {
      return null;
    }

    return {
      ...(base || { id, defaultQty: 1, kategorie: "custom" }),
      ...override,
      id,
      nazev:
        override?.nazev !== undefined && override?.nazev !== ""
          ? override.nazev
          : (base?.nazev || id),
      cenaBezDph:
        override?.cenaBezDph !== undefined && override?.cenaBezDph !== ""
          ? Number(override.cenaBezDph) || 0
          : Number(base?.cenaBezDph) || 0,
      dph:
        override?.dph !== undefined && override?.dph !== ""
          ? Number(override.dph) || 0
          : Number(base?.dph) || 0,
      jednotka:
        override?.jednotka !== undefined && override?.jednotka !== ""
          ? override.jednotka
          : (base?.jednotka || "ks"),
      defaultQty:
        override?.defaultQty !== undefined && override?.defaultQty !== ""
          ? Number(override.defaultQty) || 1
          : Number(base?.defaultQty) || 1,
      __custom: override?.__custom === true,
    };
  };
  const sluzby = Array.isArray(order?.sluzby) ? order.sluzby : [];

    const getCustomServiceIdsForSection = (sectionKey, subgroupKey = "") => {
      return Object.entries(order?.cenikOverrides || {})
        .filter(([id, value]) => {
          if (!value || typeof value !== "object" || value.__custom !== true) return false;
          if (String(value?.sekceKey || "ostatni") !== String(sectionKey || "")) return false;
          if (subgroupKey && String(value?.subgroupKey || "") !== String(subgroupKey || "")) return false;
          if (!subgroupKey && String(value?.subgroupKey || "").trim()) return false;
          return !!resolveCenikItem(id);
        })
        .map(([id]) => id)
        .sort((a, b) => {
          const na = String(resolveCenikItem(a)?.nazev || "");
          const nb = String(resolveCenikItem(b)?.nazev || "");
          return na.localeCompare(nb, "cs");
        });
    };
    const rakveList = useMemo(() => getRakveList(), []);

  const setSluzby = (next) =>
    setOrder((prev) => ({
      ...(prev || {}),
      id: (prev && prev.id) || currentOrderId || "",
      orderId: (prev && prev.orderId) || currentOrderId || "",
      zakazkaId: (prev && prev.zakazkaId) || currentOrderId || "",
      sluzby: Array.isArray(next) ? next : [],
    }));

  const getRow = (id) => {
    const found = sluzby.find((s) => s?.id === id);
    return found || { id, mnozstvi: 0, cenaBezDphOverride: "", rakevId: "" };
  };

  const toggle = (id) => {
    const cenik = resolveCenikItem(id);
    if (!cenik) return;

    const row = getRow(id);
    const qty = Number(row.mnozstvi) || 0;

    if (qty > 0) {
      setSluzby(sluzby.filter((s) => s.id !== id));
      return;
    }

    let targetQty = Number(cenik.defaultQty ?? 1) || 1;

    if (id === "chlazeni_24h" || id === "mrazeni_24h") {
      const calc = calcCoolingDays(order);
      if (id === "chlazeni_24h") {
        targetQty = Number(calc?.dnyChlad) || 0;
      }
      if (id === "mrazeni_24h") {
        targetQty = Number(calc?.dnyMraz) || 0;
      }
      if (targetQty <= 0) return;
    }

    setSluzby([
      ...sluzby,
      {
        id,
        mnozstvi: targetQty,
        cenaBezDphOverride: "",
        rakevId: "",
      },
    ]);
  };

  const setMnozstvi = (id, mnozstvi) => {
    const qty = Number(mnozstvi) || 0;
    const row = getRow(id);
    const next = sluzby.filter((s) => s.id !== id);
    if (qty > 0) next.push({ ...row, id, mnozstvi: qty });
    setSluzby(next);
  };

  const setCenaOverride = (id, value) => {
    const row = getRow(id);
    const qty = Number(row.mnozstvi) || 0;
    const next = sluzby.filter((s) => s.id !== id);
    if (qty > 0) {
      next.push({
        ...row,
        id,
        mnozstvi: qty,
        cenaBezDphOverride: value,
      });
    }
    setSluzby(next);
  };

  const setRakev = (id, rakevId) => {
    const row = getRow(id);
    const qty = Number(row.mnozstvi) || 0;
    const rakevItem = resolveCenikItem(rakevId);
    const next = sluzby.filter((s) => s.id !== id);

    if (qty > 0) {
      next.push({
        ...row,
        id,
        mnozstvi: qty,
        rakevId: rakevId || "",
        cenaBezDphOverride: rakevItem ? String(rakevItem.cenaBezDph ?? "") : "",
      });
    }

    setSluzby(next);
  };

  const setDopravaMode = (mode) => {
    setOrder((prev) => ({
      ...(prev || {}),
      prevoz: {
        ...(prev?.prevoz || {}),
        dopravaMode: mode,
      },
    }));
  };

  const etapa1Km = Number(order?.prevoz?.etapy?.etapa1Km || 0) || 0;
  const etapa2Km = Number(order?.prevoz?.etapy?.etapa2Km || 0) || 0;
  const etapa3Km = Number(order?.prevoz?.etapy?.etapa3Km || 0) || 0;
  const etapyKmCelkem = etapa1Km + etapa2Km + etapa3Km;
  const typZakazky = String(order?.zaklad?.typZakazky || "");
  const dopravaMode = String(order?.prevoz?.dopravaMode || "km");
  const bezObraduScenar = String(order?.prevoz?.bezObraduScenar || "").trim();
  const coolingScenario = String(order?.prevoz?.coolingScenario || "").trim();
  const serviceRules = getRulesForTypZakazky(typZakazky);
  const navazujiciObradSUrnou = !!order?.zaklad?.navazujiciObradSUrnou;
  const selectedTemplateId = String(order?.zaklad?.selectedTemplateId || order?.meta?.selectedTemplateId || "").trim();
  const selectedTemplateName = String(order?.meta?.selectedTemplateName || "").trim();
  const hasSelectedTemplate = !!selectedTemplateId;
  const coolingCalc = useMemo(() => calcCoolingDays(order), [order]);

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return (Array.isArray(allTemplates) ? allTemplates : []).find(
      (tpl) => String(tpl?.id || "").trim() === selectedTemplateId
    ) || null;
  }, [allTemplates, selectedTemplateId]);

  const selectedTemplateServices = useMemo(() => {
    const rows = Array.isArray(selectedTemplate?.data?.sluzby) ? selectedTemplate.data.sluzby : [];
    return rows
      .map((row) => ({
        id: String(row?.id || "").trim(),
        mnozstvi: Number(row?.mnozstvi || 0) || 0,
        cenaBezDphOverride:
          row?.cenaBezDphOverride === 0 || row?.cenaBezDphOverride === "0"
            ? row.cenaBezDphOverride
            : (row?.cenaBezDphOverride || ""),
        rakevId: row?.rakevId || "",
      }))
      .filter((row) => row.id && row.mnozstvi > 0)
      .filter((row) => isServiceAllowedForTyp(typZakazky, row.id))
      .filter((row) => !!resolveCenikItem(row.id));
  }, [selectedTemplate, typZakazky]);

  const allowedDopravaModes = useMemo(() => {
    if (typZakazky === "kremace") {
      return ["km", "pausal_cb"];
    }

    if (typZakazky === "kremace_bez_obradu") {
      if (bezObraduScenar === "doma_nebo_mimo_ck_nemocnici") {
        return ["combo_cb", "km", "pausal_cb"];
      }
      return ["pausal_cb", "km"];
    }
    if (typZakazky === "pohreb" || typZakazky === "prevoz" || typZakazky === "alternativa" || typZakazky === "obrad_s_urnou") {
      return ["km", "pausal_ck"];
    }
    return ["km"];
  }, [typZakazky]);

  const visibleLeftColumn = useMemo(
    () => LEFT_COLUMN.filter((id) => isServiceAllowedForTyp(typZakazky, id)),
    [typZakazky]
  );

  const visibleRightColumn = useMemo(
    () => RIGHT_COLUMN.filter((id) => isServiceAllowedForTyp(typZakazky, id)),
    [typZakazky]
  );

  const activeSections = useMemo(() => {
    const predefinedBase = SLUZBY_SECTIONS?.[typZakazky];
    if (!Array.isArray(predefinedBase) || !predefinedBase.length) return null;

    let merged = [...predefinedBase];

    if (typZakazky === "kremace_bez_obradu" && navazujiciObradSUrnou) {
      merged = [...merged, ...(KREMACE_BEZ_OBRADU_NAVAZUJICI_URNA_SECTIONS || [])];
    }

    return merged
      .map((section) => ({
        ...section,
        ids: Array.isArray(section?.ids)
          ? section.ids.filter((id) => isServiceAllowedForTyp(typZakazky, id))
          : [],
      }))
      .filter((section) => Array.isArray(section.ids) && section.ids.length > 0);
  }, [typZakazky, navazujiciObradSUrnou]);

  const [openSections, setOpenSections] = useState([]);

  const [openSubgroups, setOpenSubgroups] = useState({});
  const [serviceSearch, setServiceSearch] = useState("");

  useEffect(() => {
    if (!Array.isArray(activeSections) || !activeSections.length) {
      setOpenSubgroups({});
      return;
    }

    setOpenSubgroups((prev) => {
      const next = { ...(prev || {}) };
      const available = [];

      for (const section of activeSections) {
        const subs = getSectionSubgroups(section.key);
        if (Array.isArray(subs) && subs.length) {
          for (const sub of subs) {
            const k = `${section.key}::${sub.key}`;
            available.push(k);
            if (typeof next[k] !== "boolean") next[k] = false;
          }
        }
      }

      const defaultKeys = getDefaultOpenSubgroupKeys(typZakazky).filter((k) => available.includes(k));

      for (const k of defaultKeys) {
        if (!(k in (prev || {}))) next[k] = true;
      }

      return next;
    });
  }, [activeSections, typZakazky]);

  const toggleSubgroupOpen = (sectionKey, subgroupKey) => {
    const k = `${sectionKey}::${subgroupKey}`;
    setOpenSubgroups((prev) => ({
      ...(prev || {}),
      [k]: !prev?.[k],
    }));
  };


  useEffect(() => {
    if (Array.isArray(activeSections) && activeSections.length > 0) {
      setOpenSections((prev) => {
        const allowed = activeSections.map((s) => s.key);
        const kept = Array.isArray(prev) ? prev.filter((key) => allowed.includes(key)) : [];

        if (kept.length) return kept;

        const defaults = getDefaultOpenSectionKeys(typZakazky).filter((key) => allowed.includes(key));
        return defaults.length ? defaults : [activeSections[0].key];
      });
    } else {
      setOpenSections([]);
    }
  }, [typZakazky, activeSections]);

  const toggleSectionOpen = (key) => {
    setOpenSections((prev) =>
      prev.includes(key)
        ? prev.filter((item) => item !== key)
        : [...prev, key]
    );
  };

  const matchesServiceSearch = (id) => {
    const needle = String(serviceSearch || "").trim().toLowerCase();
    if (!needle) return true;

    const cenik = resolveCenikItem(id);
    if (!cenik) return false;

    const hay = [
      id,
      cenik?.nazev,
      cenik?.jednotka,
      cenik?.sekceKey,
      cenik?.subgroupKey,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return hay.includes(needle);
  };

  const getVisibleIdsForSection = (section) => {
    const subgroupDefs = getSectionSubgroups(section?.key);
    const allowedIds = Array.isArray(section?.ids) ? section.ids : [];
    const customIdsWithoutSubgroup = getCustomServiceIdsForSection(section?.key)
      .filter((id) => !allowedIds.includes(id));

    if (!Array.isArray(subgroupDefs) || !subgroupDefs.length) {
      return [...allowedIds, ...customIdsWithoutSubgroup].filter(matchesServiceSearch);
    }

    const ids = [];

    subgroupDefs.forEach((sub) => {
      const subIds = (Array.isArray(sub?.ids) ? sub.ids : [])
        .filter((id) => allowedIds.includes(id))
        .filter(matchesServiceSearch);

      const customIdsForSubgroup = getCustomServiceIdsForSection(section?.key, sub?.key)
        .filter((id) => !subIds.includes(id))
        .filter(matchesServiceSearch);

      ids.push(...subIds, ...customIdsForSubgroup);
    });

    const remaining = allowedIds
      .filter((id) => !ids.includes(id))
      .filter(matchesServiceSearch);

    ids.push(...remaining);

    const extraCustom = customIdsWithoutSubgroup.filter(matchesServiceSearch);
    ids.push(...extraCustom);

    return [...new Set(ids)];
  };

  const renderSectionRows = (section) => {
    const subgroupDefs = getSectionSubgroups(section.key);
    const allowedIds = Array.isArray(section?.ids) ? section.ids : [];
    const customIdsWithoutSubgroup = getCustomServiceIdsForSection(section.key)
      .filter((id) => !allowedIds.includes(id));

    if (!Array.isArray(subgroupDefs) || !subgroupDefs.length) {
      return [...allowedIds, ...customIdsWithoutSubgroup].map(renderRow);
    }

    const handled = new Set();
    const blocks = [];

    subgroupDefs.forEach((sub) => {
      const subIds = (Array.isArray(sub?.ids) ? sub.ids : []).filter((id) => allowedIds.includes(id));
      const customIdsForSubgroup = getCustomServiceIdsForSection(section.key, sub.key)
        .filter((id) => !subIds.includes(id));
      if (!subIds.length && !customIdsForSubgroup.length) return;

      subIds.forEach((id) => handled.add(id));
      const openKey = `${section.key}::${sub.key}`;
      const isOpen = !!openSubgroups?.[openKey];

      blocks.push(
        <React.Fragment key={openKey}>
          <tr>
            <td colSpan={8} style={{ padding: 0, background: "#fff" }}>
              <button
                type="button"
                onClick={() => toggleSubgroupOpen(section.key, sub.key)}
                style={{
                  width: "100%",
                  border: "0",
                  background: "#f9fafb",
                  borderTop: "1px solid #e5e7eb",
                  borderBottom: "1px solid #e5e7eb",
                  padding: "10px 12px",
                  textAlign: "left",
                  fontSize: 14,
                  fontWeight: 800,
                  color: "#111827",
                  cursor: "pointer",
                }}
              >
                {isOpen ? "▾" : "▸"} {sub.title}
              </button>
            </td>
          </tr>
          {isOpen ? [...subIds, ...customIdsForSubgroup].map(renderRow) : null}
        </React.Fragment>
      );
    });

    const remaining = allowedIds.filter((id) => !handled.has(id));
    if (remaining.length) {
      blocks.push(...remaining.map(renderRow));
    }

    if (customIdsWithoutSubgroup.length) {
      blocks.push(...customIdsWithoutSubgroup.map(renderRow));
    }

    return blocks;
  };


  const totals = useMemo(() => {
      return sluzby.reduce(
        (acc, row) => {
          const cenik = resolveCenikItem(row?.id);
          if (!cenik) return acc;

          const vybranaRakev = row?.rakevId ? resolveCenikItem(row.rakevId) : null;
          const incompleteRakev = isIncompleteRakevRow(row);
          const cenaAktualni =
            row?.id === "rakev" && vybranaRakev
              ? Number(vybranaRakev.cenaBezDph) || 0
              : hasManualPrice(row?.cenaBezDphOverride)
                ? Number(row.cenaBezDphOverride) || 0
                : incompleteRakev
                  ? 0
                  : Number(cenik.cenaBezDph) || 0;

          const sum = calcRow(cenaAktualni, cenik.dph, row?.mnozstvi);
          acc.bez += sum.bez;
          acc.dph += sum.dph;
          acc.celkem += sum.celkem;
          return acc;
        },
        { bez: 0, dph: 0, celkem: 0 }
      );
    }, [sluzby]);

  const invalidPriceCount = useMemo(() => {
      return sluzby.reduce((acc, row) => {
        const cenik = resolveCenikItem(row?.id);
        if (!cenik) return acc;

        const vybranaRakev = row?.rakevId ? resolveCenikItem(row.rakevId) : null;
        const incompleteRakev = isIncompleteRakevRow(row);
        const cenaAktualni =
          row?.id === "rakev" && vybranaRakev
            ? Number(vybranaRakev.cenaBezDph) || 0
            : hasManualPrice(row?.cenaBezDphOverride)
              ? Number(row.cenaBezDphOverride) || 0
              : incompleteRakev
                ? 0
                : Number(cenik.cenaBezDph) || 0;

        return acc + ((incompleteRakev || isInvalidServicePrice(cenaAktualni)) ? 1 : 0);
      }, 0);
    }, [sluzby]);


  useEffect(() => {
    if (!hasSelectedTemplate) return;
    if (!selectedTemplateServices.length) return;

    const current = Array.isArray(order?.sluzby) ? order.sluzby : [];
    const hasAnyCurrentRows = current.some((s) => String(s?.id || "").trim());

    if (hasAnyCurrentRows) return;

    setSluzby(selectedTemplateServices);
  }, [
    hasSelectedTemplate,
    selectedTemplateServices,
    order?.sluzby,
  ]);

  useEffect(() => {
    const current = Array.isArray(sluzby) ? sluzby : [];
    const coolingQty = Number(coolingCalc?.dnyChlad || 0) || 0;
    const freezingQty = Number(coolingCalc?.dnyMraz || 0) || 0;

    let changed = false;

    let next = current.map((row) => {
      const sid = String(row?.id || "").trim();

      if (sid === "chlazeni_24h") {
        if ((Number(row?.mnozstvi) || 0) !== coolingQty) {
          changed = true;
          return { ...row, mnozstvi: coolingQty };
        }
      }

      if (sid === "mrazeni_24h") {
        if ((Number(row?.mnozstvi) || 0) !== freezingQty) {
          changed = true;
          return { ...row, mnozstvi: freezingQty };
        }
      }

      return row;
    });

    next = next.filter((row) => {
      const sid = String(row?.id || "").trim();
      if (sid === "chlazeni_24h") return (Number(row?.mnozstvi) || 0) > 0;
      if (sid === "mrazeni_24h") return (Number(row?.mnozstvi) || 0) > 0;
      return true;
    });

    if (!changed && next.length === current.length) return;
    setSluzby(next);
  }, [sluzby, coolingCalc?.dnyChlad, coolingCalc?.dnyMraz]);

  useEffect(() => {
    let defaults = getDefaultServiceIdsForTyp(typZakazky);
    const contextDefaults = getContextServiceIds(order);

    if (typZakazky === "kremace_bez_obradu" && navazujiciObradSUrnou) {
      defaults = [
        ...defaults,
        "manipulace_urnou",
        "rizeni_pohrebu",
        "sjednani_obradu",
      ];
    }

    defaults = Array.from(new Set([...defaults, ...contextDefaults].filter(Boolean)));

    if (!defaults.length) return;

    const current = Array.isArray(order?.sluzby) ? order.sluzby : [];
    const currentIds = new Set(current.map((s) => String(s?.id || "").trim()).filter(Boolean));

    const protectedAutoIds = [
      "chlazeni_24h",
      "mrazeni_24h",
      "doprava_1km",
      "prevoz_pausal_ck",
      "prevoz_krematorium_cb",
      "reprodukovana_hudba",
      "reprodukovana_hudba_mimo_sin",
      "civilni_recnik_v_sini",
      "civilni_recnik_mimo_ck",
      "alternativni_misto_organizace",
    ];

    const hasManualRows = current.some((s) => {
      const sid = String(s?.id || "").trim();
      return sid && !protectedAutoIds.includes(sid);
    });

    const hasAnyCurrentRows = current.some((s) => String(s?.id || "").trim());

    if (hasSelectedTemplate && hasAnyCurrentRows) return;
    if (hasManualRows) return;

    const allowedDefaults = defaults
      .filter((id) => isServiceAllowedForTyp(typZakazky, id))
      .filter((id) => resolveCenikItem(id));

    const next = current.filter((row) => {
      const sid = String(row?.id || "").trim();
      if (!protectedAutoIds.includes(sid)) return true;

      if (sid === "chlazeni_24h") {
        return allowedDefaults.includes(sid) && Number(coolingCalc?.dnyChlad || 0) > 0;
      }

      if (sid === "mrazeni_24h") {
        return allowedDefaults.includes(sid) && Number(coolingCalc?.dnyMraz || 0) > 0;
      }

      if (["doprava_1km", "prevoz_pausal_ck", "prevoz_krematorium_cb"].includes(sid)) return true;
      return allowedDefaults.includes(sid);
    });

    const nextIds = new Set(next.map((s) => String(s?.id || "").trim()).filter(Boolean));

    const coolingMap = {
      chlazeni_24h: Number(coolingCalc?.dnyChlad || 0) || 0,
      mrazeni_24h: Number(coolingCalc?.dnyMraz || 0) || 0,
    };

    const toAdd = allowedDefaults
      .filter((id) => !nextIds.has(id))
      .map((id) => {
        const cenik = resolveCenikItem(id);
        let qty = Number(cenik?.defaultQty ?? 1) || 1;

        if (id in coolingMap) {
          qty = Number(coolingMap[id]) || 0;
        }

        if (qty <= 0) return null;

        return {
          id,
          mnozstvi: qty,
          cenaBezDphOverride: "",
          rakevId: "",
        };
      })
      .filter(Boolean);

    const changed =
      next.length !== current.length ||
      toAdd.length > 0 ||
      allowedDefaults.some((id) => protectedAutoIds.includes(id) && !currentIds.has(id));

    if (!changed) return;

    setSluzby([...next, ...toAdd]);
  }, [
    typZakazky,
    navazujiciObradSUrnou,
    order?.obrad?.mistoId,
    order?.obrad?.mistoSkupina,
    order?.obrad?.ceremonyVariant,
    order?.obrad?.churchServiceType,
    order?.obrad?.fararJmeno,
    order?.obrad?.recnik,
    order?.obrad?.hudba,
    order?.obrad?.misto,
    coolingCalc?.dnyChlad,
    coolingCalc?.dnyMraz,
    hasSelectedTemplate,
  ]);

  useEffect(() => {
    const currentMode = String(order?.prevoz?.dopravaMode || "").trim();
    if (currentMode) return;

    const nextMode = getDefaultDopravaMode(typZakazky, order);
    setOrder((prev) => ({
      ...(prev || {}),
      prevoz: {
        ...(prev?.prevoz || {}),
        dopravaMode: nextMode,
      },
    }));
  }, [typZakazky, order?.prevoz?.dopravaMode, order?.prevoz?.bezObraduScenar, setOrder]); // AUTO_DOPRAVA_DEFAULT

  useEffect(() => {
    if (!allowedDopravaModes.includes(dopravaMode)) {
      const fallback = getDefaultDopravaMode(typZakazky, order);
      const safeMode = allowedDopravaModes.includes(fallback) ? fallback : allowedDopravaModes[0] || "km";

      setOrder((prev) => ({
        ...(prev || {}),
        prevoz: {
          ...(prev?.prevoz || {}),
          dopravaMode: safeMode,
        },
      }));
    }
  }, [allowedDopravaModes, dopravaMode, typZakazky, order?.prevoz?.bezObraduScenar, setOrder]); // AUTO_DOPRAVA_GUARD

    useEffect(() => {
      const { dnyChlad, dnyMraz, ok } = calcCoolingDays(order);

      const current = Array.isArray(sluzby) ? sluzby : [];
      const autoCoolingIds = ["chlazeni_24h", "mrazeni_24h"];
      const autoTransportIds = ["doprava_1km", "prevoz_pausal_ck", "prevoz_krematorium_cb"];
      const autoManagedIds = [...autoCoolingIds, ...autoTransportIds];

      const next = current.filter((s) => !autoManagedIds.includes(String(s?.id || "").trim()));

      if (ok && dnyChlad > 0) {
        next.push({
          id: "chlazeni_24h",
          mnozstvi: dnyChlad,
          cenaBezDphOverride: "",
        });
      }

      if (ok && dnyMraz > 0) {
        next.push({
          id: "mrazeni_24h",
          mnozstvi: dnyMraz,
          cenaBezDphOverride: "",
        });
      }

      const currentTransportRows = current.filter(
        (s) => autoTransportIds.includes(String(s?.id || "").trim()) && (Number(s?.mnozstvi) || 0) > 0
      );

      const shouldPreserveTemplateTransport =
        hasSelectedTemplate && currentTransportRows.length > 0;

      if (shouldPreserveTemplateTransport) {
        currentTransportRows.forEach((s) => next.push(s));
      } else if (etapyKmCelkem > 0) {
        if (dopravaMode === "km") {
          next.push({
            id: "doprava_1km",
            mnozstvi: etapyKmCelkem,
            cenaBezDphOverride: "",
          });
        }

        if (dopravaMode === "pausal_ck") {
          next.push({
            id: "prevoz_pausal_ck",
            mnozstvi: 1,
            cenaBezDphOverride: "",
          });
        }

        if (dopravaMode === "pausal_cb") {
          next.push({
            id: "prevoz_krematorium_cb",
            mnozstvi: 1,
            cenaBezDphOverride: "",
          });
        }

        if (dopravaMode === "combo_cb") {
          if (etapa1Km > 0) {
            next.push({
              id: "doprava_1km",
              mnozstvi: etapa1Km,
              cenaBezDphOverride: "",
            });
          }

          next.push({
            id: "prevoz_krematorium_cb",
            mnozstvi: 1,
            cenaBezDphOverride: "",
          });
        }
      } else {
        currentTransportRows.forEach((s) => next.push(s));
      }

      const normalizeAutoRows = (rows) =>
        rows
          .filter((s) => autoManagedIds.includes(String(s?.id || "").trim()))
          .map((s) => ({
            id: String(s?.id || "").trim(),
            mnozstvi: Number(s?.mnozstvi || 0) || 0,
          }))
          .sort((a, b) => String(a.id).localeCompare(String(b.id)));

      const prevKey = JSON.stringify(normalizeAutoRows(current));
      const nextKey = JSON.stringify(normalizeAutoRows(next));

      if (prevKey === nextKey) return;
      setSluzby(next);
    }, [
      order?.vstupniInformace?.datumUmrti,
      order?.vstupniInformace?.casUmrti,
      order?.zesnulaOsoba?.datumUmrti,
      order?.zesnulaOsoba?.casUmrti,
      order?.prevoz?.datumDoChlazeni,
      order?.prevoz?.casDoChlazeni,
      order?.prevoz?.datumUlozeni,
      order?.prevoz?.casUlozeni,
      order?.prevoz?.datumPrevozu,
      order?.prevoz?.casPrevozu,
      order?.prevoz?.etapy?.etapa1Km,
      order?.prevoz?.etapy?.etapa2Km,
      order?.prevoz?.etapy?.etapa3Km,
      etapa1Km,
      etapyKmCelkem,
      dopravaMode,
      hasSelectedTemplate,
      order?.prevoz?.bezObraduScenar,
      order?.obrad?.datum,
      order?.obrad?.cas,
    ]);

  const renderRow = (id) => {
      const cenik = resolveCenikItem(id);
      if (!cenik) return null;

      const row = getRow(id);
      const qty = Number(row.mnozstvi) || 0;
      const jednotka = cenik.jednotka || "ks";
      const vybranaRakev = row.rakevId ? resolveCenikItem(row.rakevId) : null;
      const incompleteRakev = isIncompleteRakevRow(row);
      const cenaAktualni =
        id === "rakev" && vybranaRakev
          ? Number(vybranaRakev.cenaBezDph) || 0
          : hasManualPrice(row.cenaBezDphOverride)
            ? Number(row.cenaBezDphOverride) || 0
            : incompleteRakev
              ? 0
              : Number(cenik.cenaBezDph) || 0;
      const invalidPrice = incompleteRakev || isInvalidServicePrice(cenaAktualni);
    const zobrazovanyNazev =
      id === "rakev" && vybranaRakev
        ? `${cenik.nazev} – ${vybranaRakev.nazev}`
        : cenik.nazev;
    const sum = calcRow(cenaAktualni, cenik.dph, qty);

    return (
      <tr key={id} style={invalidPrice ? { background: "#fff7f7" } : undefined}>
        <td style={{ ...tdStyle, textAlign: "center", width: 54 }}>
          <input
            type="checkbox"
            checked={qty > 0}
            onChange={() => toggle(id)}
            style={{ width: 20, height: 20 }}
          />
        </td>
        <td style={tdStyle}>{zobrazovanyNazev}</td>
        <td style={{ ...tdStyle, whiteSpace: "nowrap", width: 180 }}>
          {id === "rakev" ? (
            <div style={{ display: "grid", gap: 8 }}>
              <select
                value={row.rakevId || ""}
                onChange={(e) => setRakev(id, e.target.value)}
                style={{ ...inputStyle, width: 170 }}
              >
                <option value="">Vyber rakev</option>
                {rakveList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nazev} – {formatMoney(r.cenaBezDph)}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: incompleteRakev ? "#b91c1c" : "#6b7280", fontWeight: incompleteRakev ? 800 : 400 }}>
                  {vybranaRakev
                    ? `Cena rakve: ${formatMoney(vybranaRakev.cenaBezDph)}`
                    : hasManualPrice(row.cenaBezDphOverride)
                      ? `Ruční cena rakve: ${formatMoney(row.cenaBezDphOverride)}`
                      : "Nejprve vyber rakev nebo zadej cenu ručně"}
                </div>
            </div>
          ) : (
            <input
              type="number"
              min="0"
              step="0.01"
              value={row.cenaBezDphOverride !== "" ? row.cenaBezDphOverride : cenaAktualni}
              onChange={(e) => setCenaOverride(id, e.target.value)}
              style={{
                ...inputStyle,
                width: 110,
                border: invalidPrice ? "1px solid #dc2626" : inputStyle.border,
                background: invalidPrice ? "#fff1f2" : "#fff",
                color: invalidPrice ? "#991b1b" : "#111827",
                fontWeight: invalidPrice ? 800 : 400,
              }}
            />
          )}
        {invalidPrice ? (
            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: "#b91c1c" }}>
              {incompleteRakev
                ? "Pozor: u rakve chybí výběr konkrétní rakve nebo ruční cena"
                : "Pozor: cena je 0 Kč nebo neplatná"}
            </div>
          ) : null}
        </td>
        <td style={{ ...tdStyle, whiteSpace: "nowrap", width: 70 }}>
          {cenik.dph} %
        </td>
        <td style={{ ...tdStyle, whiteSpace: "nowrap", width: 130 }}>
          <input
            type="number"
            min="0"
            step="1"
            value={qty}
            onChange={(e) => setMnozstvi(id, e.target.value)}
            style={inputStyle}
          />{" "}
          {jednotka}
        </td>
        <td style={{ ...tdStyle, whiteSpace: "nowrap", width: 120 }}>
          {formatMoney(sum.bez)}
        </td>
        <td style={{ ...tdStyle, whiteSpace: "nowrap", width: 120 }}>
          {formatMoney(sum.dph)}
        </td>
        <td style={{ ...tdStyle, whiteSpace: "nowrap", width: 120, fontWeight: 800 }}>
          {formatMoney(sum.celkem)}
        </td>
      </tr>
    );
  };

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>Výběr služeb</div>
      <div style={subStyle}>
        Výběr položek z ceníku pro konkrétní zakázku. Základ pro cenu, finální objednávku a dokumenty.
      </div>

      {etapyKmCelkem > 0 ? (

      <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Způsob účtování dopravy</div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {allowedDopravaModes.includes("km") ? (
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="dopravaMode"
                  checked={dopravaMode === "km"}
                  onChange={() => setDopravaMode("km")}
                />
                Skutečné km ({etapyKmCelkem} km)
              </label>
            ) : null}

            {allowedDopravaModes.includes("pausal_ck") ? (
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="dopravaMode"
                  checked={dopravaMode === "pausal_ck"}
                  onChange={() => setDopravaMode("pausal_ck")}
                />
                Paušál po ČK
              </label>
            ) : null}

            {allowedDopravaModes.includes("pausal_cb") ? (
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="radio"
                  name="dopravaMode"
                  checked={dopravaMode === "pausal_cb"}
                  onChange={() => setDopravaMode("pausal_cb")}
                />
                Paušál krematorium ČB
              </label>
            ) : null}
          </div>
          <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280" }}>
            Aktuálně vybráno: <strong>{getDopravaModeLabel(dopravaMode)}</strong>
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
            Doporučený režim pro tuto zakázku: <strong>{getDopravaModeLabel(getDefaultDopravaMode(typZakazky, order))}</strong>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, color: "#374151", fontWeight: 700 }}>
            Scénář chlazení / mražení
          </div>
          <select
            value={coolingScenario}
            onChange={(e) =>
              setOrder((prev) => ({
                ...(prev || {}),
                prevoz: {
                  ...(prev?.prevoz || {}),
                  coolingScenario: String(e.target.value || ""),
                },
              }))
            }
            style={{ ...inputStyle, width: 320, marginTop: 6 }}
          >
            <option value="">Standard</option>
            <option value="wagnerka">Úmrtí Wagnerka</option>
          </select>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
            Tento scénář připravuje odlišnou logiku pro automatické chlazení a mražení.
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
            Chlazení vypočteno: <strong>{Number(coolingCalc?.dnyChlad || 0)}</strong> dnů
            {" "}• Mražení vypočteno: <strong>{Number(coolingCalc?.dnyMraz || 0)}</strong> dnů
            {" "}• Režim: <strong>{coolingCalc?.scenario || "standard"}</strong>
          </div>
          {typZakazky === "kremace_bez_obradu" ? (
            <>
              <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
                Scénář převozu bez obřadu: <strong>{String(order?.prevoz?.bezObraduScenar || "-")}</strong>
              </div>
              {dopravaMode === "combo_cb" ? (
                <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
                  Kombinace účtování: etapa 1 v km + paušál do krematoria ČB
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Aktivní režim služeb</div>
        <div style={{ fontSize: 14, color: "#111827", fontWeight: 800 }}>
          {serviceRules?.title || "Bez omezení"}
        </div>
        <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
          Zobrazeny jsou jen služby, které se týkají zvoleného typu zakázky.
        </div>
        {hasSelectedTemplate ? (
          <div style={{ marginTop: 8, fontSize: 13, color: "#1d4ed8", fontWeight: 700 }}>
            Aktivní vzor: <strong>{selectedTemplateName || selectedTemplateId}</strong>
            {selectedTemplateServices.length ? (
              <span style={{ color: "#475569", fontWeight: 600 }}>
                {" "}• služeb ze vzoru: {selectedTemplateServices.length}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Souhrn</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span style={badgeStyle}>Vybráno služeb: {sluzby.length}</span>
          <span style={badgeStyle}>Bez DPH: {formatMoney(totals.bez)}</span>
          <span style={badgeStyle}>DPH: {formatMoney(totals.dph)}</span>
          <span style={badgeStyle}>Celkem: {formatMoney(totals.celkem)}</span>
          {invalidPriceCount ? (
            <span
              style={{
                ...badgeStyle,
                background: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
              }}
            >
              Podezřelé ceny: {invalidPriceCount}
            </span>
          ) : null}
        </div>
        {invalidPriceCount ? (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              borderRadius: 12,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            Pozor: ve Výběru služeb jsou {invalidPriceCount} položky s nulovou nebo neplatnou cenou.
          </div>
        ) : null}

      </div>        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Položky služeb</div>
          <div style={{ marginBottom: 14 }}>
            <input
              value={serviceSearch}
              onChange={(e) => setServiceSearch(e.target.value)}
              placeholder="Hledat službu podle názvu nebo ID..."
              style={{ ...inputStyle, width: "100%", maxWidth: 420, background: "#fff" }}
            />
          </div>

            {activeSections?.length ? (
              <div style={{ display: "grid", gap: 18 }}>
                {activeSections.map((section) => {
                  const visibleIds = getVisibleIdsForSection(section);
                  const visibleCount = visibleIds.length;
                  if (!visibleCount) return null;

                  const isOpen = openSections.includes(section.key);

                  return (
                    <div key={section.key} style={{ border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: "#fff" }}>
                      <button
                        type="button"
                        onClick={() => toggleSectionOpen(section.key)}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#111827",
                          background: "#f9fafb",
                          border: "none",
                          borderBottom: isOpen ? "1px solid #e5e7eb" : "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <span>{section.title}</span>
                        <span style={{ fontSize: 14, color: "#6b7280" }}>
                          {isOpen ? "Skrýt" : "Zobrazit"} ({visibleCount})
                        </span>
                      </button>

                      {isOpen ? (
                        <div style={{ overflowX: "auto" }}>
                          <table style={tableStyle}>
                            <thead>
                              <tr>
                                <th style={{ ...thStyle, textAlign: "center" }}>✔</th>
                                <th style={thStyle}>Služba</th>
                                <th style={thStyle}>Cena bez DPH</th>
                                <th style={thStyle}>DPH</th>
                                <th style={thStyle}>Množství</th>
                                <th style={thStyle}>Řádek bez DPH</th>
                                <th style={thStyle}>Řádek DPH</th>
                                <th style={thStyle}>Řádek celkem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {renderSectionRows(section)}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (() => {
              const fallbackLeft = visibleLeftColumn.filter(matchesServiceSearch);
              const fallbackRight = visibleRightColumn.filter(matchesServiceSearch);
              const fallbackCustom = getCustomServiceIdsForSection("ostatni").filter(matchesServiceSearch);
              const hasFallbackRows = [...fallbackLeft, ...fallbackRight, ...fallbackCustom].length > 0;
              const showMiddleDivider = fallbackLeft.length > 0 && fallbackRight.length > 0;
              const showCustomDivider = (fallbackLeft.length > 0 || fallbackRight.length > 0) && fallbackCustom.length > 0;

              return (
                <div style={{ overflowX: "auto" }}>
                  {!hasFallbackRows ? (
                    <div
                      style={{
                        padding: 16,
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        background: "#fff",
                        color: "#6b7280",
                        textAlign: "center",
                        marginBottom: 12,
                      }}
                    >
                      Nic neodpovídá hledání.
                    </div>
                  ) : null}

                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle, textAlign: "center" }}>✔</th>
                        <th style={thStyle}>Služba</th>
                        <th style={thStyle}>Cena bez DPH</th>
                        <th style={thStyle}>DPH</th>
                        <th style={thStyle}>Množství</th>
                        <th style={thStyle}>Řádek bez DPH</th>
                        <th style={thStyle}>Řádek DPH</th>
                        <th style={thStyle}>Řádek celkem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fallbackLeft.map(renderRow)}

                      {showMiddleDivider ? (
                        <tr>
                          <td colSpan={8} style={{ background: "#f3f4f6", height: 10 }} />
                        </tr>
                      ) : null}

                      {fallbackRight.map(renderRow)}

                      {showCustomDivider ? (
                        <tr>
                          <td colSpan={8} style={{ background: "#f3f4f6", height: 10 }} />
                        </tr>
                      ) : null}

                      {fallbackCustom.map(renderRow)}
                    </tbody>
                  </table>
                </div>
              );
            })()}
        </div>
    </div>
  );
}
